import {
  ADD_TO_WISHLIST,
  DELETE_FROM_WISHLIST,
  DELETE_ALL_FROM_WISHLIST,
  SET_WISHLIST_FROM_BACKEND,
  WISHLIST_LOADING,
  WISHLIST_ERROR,
  UPSERT_WISHLIST_ITEM,
  REMOVE_WISHLIST_ITEM
} from "../actions/wishlistActions";
import { normalizeMediaUrl, normalizeProduct } from "../../utils/media";

// Initial state with loading and error states
const initState = {
  items: [],
  loading: false,
  error: null,
  count: 0
};
const DEFAULT_PRODUCT_IMAGE = '/assets/img/product/hm6-pro-1.jpg';

const normalizeWishlistItem = (item) => {
  const normalizedProduct = item.product ? normalizeProduct(item.product) : item.product;
  const productId = typeof normalizedProduct === 'object' && normalizedProduct !== null
    ? normalizedProduct.id
    : item.product;
  const priceValue = normalizedProduct?.price || item.product_price || 0;
  const imageCandidate = normalizedProduct?.images?.[0]?.image_url ||
    normalizedProduct?.images?.[0]?.image ||
    normalizedProduct?.highlight_image?.image_url ||
    normalizedProduct?.highlight_image?.image ||
    item.image;
  const normalizedImage = normalizeMediaUrl(imageCandidate);

  return {
    ...item,
    wishlistItemId: item.id,
    id: productId || item.product_id || item.id,
    name: normalizedProduct?.name,
    slug: normalizedProduct?.slug,
    price: parseFloat(priceValue || 0),
    image: normalizedImage ? [normalizedImage] : [DEFAULT_PRODUCT_IMAGE],
    brand: normalizedProduct?.brand,
    added_at: item.added_at,
    product: normalizedProduct
  };
};

const normalizeId = (value) => (value === undefined || value === null ? "" : String(value));

const wishlistReducer = (state = initState, action) => {
  // Handle both old array format and new object format
  const wishlistItems = Array.isArray(state) ? state : (state.items || []);
  const product = action.payload;

  switch (action.type) {
    case SET_WISHLIST_FROM_BACKEND:
      // Transform backend wishlist items to match expected format
      const backendItems = (action.payload.items || []).map(normalizeWishlistItem);

      return {
        items: backendItems,
        loading: false,
        error: null,
        count: action.payload.count || backendItems.length
      };
    case UPSERT_WISHLIST_ITEM: {
      const normalizedItem = normalizeWishlistItem(action.payload);
      const normalizedItemId = normalizeId(normalizedItem.id);
      const existingIndex = wishlistItems.findIndex(item => normalizeId(item.id) === normalizedItemId);
      const updatedItems = existingIndex >= 0
        ? wishlistItems.map(item => (normalizeId(item.id) === normalizedItemId ? normalizedItem : item))
        : [...wishlistItems, normalizedItem];

      return {
        ...state,
        items: updatedItems,
        loading: false,
        error: null,
        count: updatedItems.length
      };
    }
    case REMOVE_WISHLIST_ITEM: {
      const productId = action.payload?.productId;
      const normalizedProductId = normalizeId(productId);
      const filteredItems = productId !== undefined && productId !== null
        ? wishlistItems.filter(item =>
            normalizeId(item.id) !== normalizedProductId &&
            normalizeId(item.product?.id) !== normalizedProductId
          )
        : wishlistItems;

      return {
        ...state,
        items: filteredItems,
        loading: false,
        error: null,
        count: filteredItems.length
      };
    }

    case WISHLIST_LOADING:
      return {
        ...state,
        items: Array.isArray(state) ? state : (state.items || []),
        loading: action.payload,
        error: null
      };

    case WISHLIST_ERROR:
      return {
        ...state,
        items: Array.isArray(state) ? state : (state.items || []),
        loading: false,
        error: action.payload
      };

    case ADD_TO_WISHLIST:
      // Legacy: add to local wishlist
      const existingItem = wishlistItems.find(item => normalizeId(item.id) === normalizeId(product.id));
      if (existingItem === undefined) {
        const newItems = [...wishlistItems, product];
        return Array.isArray(state)
          ? newItems
          : { ...state, items: newItems, count: newItems.length };
      }
      return state;

    case DELETE_FROM_WISHLIST:
      const normalizedProductId = normalizeId(product.id);
      const normalizedWishlistItemId = normalizeId(product.wishlistItemId);
      const filteredItems = wishlistItems.filter(
        wishlistItem => normalizeId(wishlistItem.id) !== normalizedProductId &&
                       normalizeId(wishlistItem.wishlistItemId) !== normalizedWishlistItemId
      );
      return Array.isArray(state)
        ? filteredItems
        : { ...state, items: filteredItems, count: filteredItems.length };

    case DELETE_ALL_FROM_WISHLIST:
      return Array.isArray(state)
        ? []
        : { ...state, items: [], count: 0 };

    default:
      // Maintain backward compatibility
      if (Array.isArray(state)) {
        return state;
      }
      return state.items ? state : { items: state, loading: false, error: null, count: 0 };
  }
};

export default wishlistReducer;
